import type { ResultsData, SessionData } from "../types/assessment";

const API_BASE = (
  import.meta.env.VITE_API_URL || "http://localhost:8787"
).replace(/\/$/, "");

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(
      res.status,
      (body as { error?: string }).error || res.statusText,
    );
  }

  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const api = {
  createSession(data: {
    email?: string;
    answers?: Record<number, string>;
    currentQuestion?: number;
    currentGroup?: number;
    locale?: string;
  }): Promise<SessionData> {
    return request("/sessions", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  resumeSession(email: string): Promise<SessionData> {
    return request("/sessions/resume", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  updateCompany(sessionId: string, companyName: string): Promise<SessionData> {
    return request(`/sessions/${sessionId}/company`, {
      method: "PUT",
      body: JSON.stringify({ companyName }),
    });
  },

  getSession(id: string): Promise<SessionData> {
    return request(`/sessions/${id}`);
  },

  saveAnswers(
    sessionId: string,
    data: {
      answers: Record<number, string>;
      currentQuestion: number;
      currentGroup: number;
    },
  ): Promise<{ ok: boolean }> {
    return request(`/sessions/${sessionId}/answers`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  getResults(sessionId: string): Promise<ResultsData> {
    return request(`/sessions/${sessionId}/results`);
  },

  pauseSession(
    sessionId: string,
    resumeLink: string,
  ): Promise<{ ok: boolean }> {
    return request(`/sessions/${sessionId}/pause`, {
      method: "POST",
      body: JSON.stringify({ resumeLink }),
    });
  },
};
